import * as Dialog from '@radix-ui/react-dialog';
import { clsx } from 'clsx';
import type { ReactNode } from 'react';

type Size = 'sm' | 'md' | 'lg' | 'xl';

const SIZE: Record<Size, string> = {
  sm: 'max-w-[400px]',
  md: 'max-w-[560px]',
  lg: 'max-w-[768px]',
  xl: 'max-w-[960px]',
};

export interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  size?: Size;
  title?: ReactNode;
  footer?: ReactNode;
  className?: string;
  children: ReactNode;
}

export function Modal({ open, onOpenChange, size = 'md', title, footer, className, children }: ModalProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        />
        <Dialog.Content
          className={clsx(
            'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
            'w-[calc(100vw-32px)]',
            SIZE[size],
            'rounded-2xl border border-line bg-bg-card text-fg-primary shadow-2xl',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            className,
          )}
        >
          {title && (
            <div className="flex items-center justify-between border-b border-line px-5 py-4">
              <Dialog.Title className="text-base font-semibold tracking-heading">{title}</Dialog.Title>
              <Dialog.Close
                aria-label="Close"
                className="text-fg-muted hover:text-fg-primary text-xl leading-none"
              >
                ×
              </Dialog.Close>
            </div>
          )}
          <div className="px-5 py-4">{children}</div>
          {footer && (
            <div className="flex items-center justify-end gap-2 border-t border-line px-5 py-3">
              {footer}
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
